import { getElements } from '../Utility/Dom'

interface ScrollAnimationOptions {
  animationClass?: string
  removeOnLeave?: boolean
  rootMargin?: string
  threshold?: number | number[]
}

class AnimationService {
  private readonly animationClass: string
  private elementsToObserve: NodeListOf<HTMLElement>
  private readonly enabled: boolean
  private readonly observer: IntersectionObserver | null = null
  private readonly removeOnLeave: boolean

  constructor(options: ScrollAnimationOptions = {}) {
    this.animationClass = options.animationClass || 'animate'
    this.removeOnLeave = options.removeOnLeave ?? true
    this.elementsToObserve = getElements('animate')
    this.enabled = this.shouldEnableAnimations()

    if (!this.enabled) {
      return
    }

    const intersectionOptions = {
      rootMargin: options.rootMargin ?? '0px',
      threshold: options.threshold ?? 0.1,
    }

    this.observer = new IntersectionObserver(
      (entries) => this.handleIntersection(entries),
      intersectionOptions
    )
  }

  public disconnect(): void {
    if (!this.observer) {
      return
    }

    this.observer.disconnect()
  }

  public isEnabled(): boolean {
    return this.enabled
  }

  public observe(): void {
    if (!this.enabled || !this.observer) {
      return
    }

    this.elementsToObserve.forEach((element) => {
      this.observer!.observe(element)
    })
  }

  public unobserve(element: Element): void {
    if (!this.enabled || !this.observer) {
      return
    }

    this.observer.unobserve(element)
  }

  private handleIntersection(entries: IntersectionObserverEntry[]): void {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add(this.animationClass)
      } else if (this.removeOnLeave) {
        entry.target.classList.remove(this.animationClass)
      }
    })
  }

  private shouldEnableAnimations(): boolean {
    const hasAnimateElements = this.elementsToObserve.length > 0
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches

    return hasAnimateElements && !prefersReducedMotion
  }
}

export default AnimationService
